import React from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ReactComponent as Dropdown } from "../../../../../assets/images/dropdown-arrow.svg";
import {
  AGENT_CHAT_LIMIT_OPTIONS,
  AGENT_KB_ROLE_OPTIONS,
  AGENT_ROLE_FILTER_OPTIONS,
  AGENT_STATUS_OPTIONS,
  AGENT_TIMEZONE_OPTIONS,
} from "../../../../../constants/peopleData";
import CustomerEditPhoto from "../../Customers/CustomerEdit/CustomerEditPhoto";
import { AgentFormFieldSkeleton } from "../AgentLoadingSkeletons";
import {
  fieldLabelSx,
  inputSx,
  pageSubtitleSx,
  pageTitleSx,
  peopleSelectSx,
  TEAM_FORM_WIDTH,
} from "../../Teams/teamFormStyles";
import { peopleSelectControlSx, peopleScrollSelectMenuProps } from "../../peopleSelectStyles";
import { FilterChipCloseIcon } from "../../Customers/customersFilterUiShared";

const columnSelectSx = peopleSelectControlSx({ width: "100%", maxWidth: "100%" });
const selectMenuProps = peopleScrollSelectMenuProps();

const STATUS_DOT_BY_VALUE = Object.fromEntries(
  AGENT_STATUS_OPTIONS.map((o) => [o.value, o.dot])
);

const STATUS_LABEL_BY_VALUE = Object.fromEntries(
  AGENT_STATUS_OPTIONS.map((o) => [o.value, o.label])
);

function SelectionChip({ label, onRemove }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        py: 0.5,
        borderRadius: "20px",
        border: "1px solid #C5CED6",
        background: "#F9FAFA",
        boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
      }}
    >
      <Typography sx={{ fontSize: 14, color: "#405261", fontFamily: "Inter" }}>{label}</Typography>
      {typeof onRemove === "function" && (
        <Box
          component="button"
          type="button"
          onClick={onRemove}
          sx={{ border: "none", background: "transparent", p: 0, display: "flex", cursor: "pointer" }}
        >
          <FilterChipCloseIcon style={{ width: 16, height: 16, display: "block" }} />
        </Box>
      )}
    </Box>
  );
}

function StatusSelect({ value, onChange, options, disabled = false }) {
  return (
    <FormControl fullWidth size="small" sx={columnSelectSx}>
      <Select
        value={value}
        onChange={onChange}
        disabled={disabled}
        IconComponent={Dropdown}
        MenuProps={selectMenuProps}
        sx={peopleSelectSx}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: STATUS_DOT_BY_VALUE[selected] || "#93A1B0",
                flexShrink: 0,
              }}
            />
            <Typography component="span" noWrap sx={{ fontSize: 14, color: "#405261" }}>
              {STATUS_LABEL_BY_VALUE[selected] || selected}
            </Typography>
          </Box>
        )}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: opt.dot,
                  flexShrink: 0,
                }}
              />
              {opt.label}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default function AgentEditForm({
  form,
  onChange,
  groupOptions = [],
  teamOptions = [],
  optionsLoading = false,
  roleDisabled = false,
}) {
  const set = (key) => (e) => onChange({ ...form, [key]: e.target.value });

  const groupLabel = (id) => groupOptions.find((g) => g.id === id)?.label || id;
  const teamLabel = (id) => teamOptions.find((t) => t.id === id)?.label || id;

  const editablePresenceOptions = AGENT_STATUS_OPTIONS.filter(
    (o) => o.value !== "suspended" && o.value !== "invitationSent"
  );
  // Keep current API status visible when it's invitationSent (or any non-editable value)
  const currentPresenceOption = AGENT_STATUS_OPTIONS.find((o) => o.value === form.presence);
  const presenceOptions =
    form.presence &&
    !editablePresenceOptions.some((o) => o.value === form.presence) &&
    currentPresenceOption
      ? [...editablePresenceOptions, currentPresenceOption]
      : editablePresenceOptions;
  const presenceValue = presenceOptions.some((o) => o.value === form.presence)
    ? form.presence
    : "accepting";

  const isSuperAdmin = form.roleName === "admin_with_billing";
  const isRoleLocked = isSuperAdmin || roleDisabled;
  const editableRoleOptions = AGENT_ROLE_FILTER_OPTIONS.filter(
    (o) => o.value === "agent" || o.value === "admin"
  );

  const handleGroupSelect = (e) => {
    const value = e.target.value;
    if (!value || (form.groupIds || []).includes(value)) return;
    onChange({
      ...form,
      groupIds: [...(form.groupIds || []), value],
      groupSelect: "",
    });
  };

  const handleTeamSelect = (e) => {
    const value = e.target.value;
    if (!value || (form.teamIds || []).includes(value)) return;
    onChange({
      ...form,
      teamIds: [...(form.teamIds || []), value],
      teamSelect: "",
    });
  };

  const handlePhotoFile = (file) => {
    const preview = URL.createObjectURL(file);
    onChange({ ...form, avatarFile: file, avatarPreview: preview });
  };

  const avatarSrc = form.avatarPreview || form.avatar;
  const timezoneValue =
    form.timezone && form.timezone !== "—"
      ? form.timezone
      : AGENT_TIMEZONE_OPTIONS[0]?.value || "";
  const timezoneOptions =
    timezoneValue &&
    !AGENT_TIMEZONE_OPTIONS.some((o) => o.value === timezoneValue)
      ? [{ value: timezoneValue, label: timezoneValue }, ...AGENT_TIMEZONE_OPTIONS]
      : AGENT_TIMEZONE_OPTIONS;
  const chatLimitValue =
    form.chatLimit && form.chatLimit !== "—" ? String(form.chatLimit) : "5";
  const isStatusLocked =
    form.presence === "suspended" || form.presence === "invitationSent";

  return (
    <Box sx={{ maxWidth: TEAM_FORM_WIDTH, width: "100%", mx: "auto" }}>
      <Typography sx={pageTitleSx}>Edit Agents</Typography>
      <Typography sx={pageSubtitleSx}>
        Manage agent profile, permissions, availability and assigned teams.
      </Typography>

      <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <CustomerEditPhoto name={form.name} avatarSrc={avatarSrc} onFileSelect={handlePhotoFile} />

        <Box>
          <Typography sx={fieldLabelSx}>Name</Typography>
          <TextField
            fullWidth
            placeholder="Enter your name"
            value={form.name}
            onChange={set("name")}
            sx={inputSx}
          />
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>Email</Typography>
          <TextField
            fullWidth
            placeholder="Enter email address"
            value={form.email}
            onChange={set("email")}
            sx={inputSx}
          />
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>Job Title</Typography>
          <TextField
            fullWidth
            placeholder="Support Specialist"
            value={form.jobTitle}
            onChange={set("jobTitle")}
            sx={inputSx}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            width: "100%",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={fieldLabelSx}>Role</Typography>
            {optionsLoading ? (
              <AgentFormFieldSkeleton />
            ) : (
              <FormControl fullWidth size="small" sx={columnSelectSx}>
                <Select
                  value={form.roleName}
                  onChange={set("roleName")}
                  disabled={isRoleLocked}
                  IconComponent={Dropdown}
                  MenuProps={selectMenuProps}
                  sx={peopleSelectSx}
                  renderValue={(selected) => (
                    <Typography component="span" noWrap sx={{ fontSize: 14, color: "#405261" }}>
                      {isSuperAdmin
                        ? "Super Admin"
                        : AGENT_ROLE_FILTER_OPTIONS.find((o) => o.value === selected)?.label ||
                          selected}
                    </Typography>
                  )}
                >
                  {isSuperAdmin ? (
                    <MenuItem value="admin_with_billing">Super Admin</MenuItem>
                  ) : isRoleLocked ? (
                    <MenuItem value={form.roleName}>
                      {AGENT_ROLE_FILTER_OPTIONS.find((o) => o.value === form.roleName)?.label ||
                        form.roleName}
                    </MenuItem>
                  ) : (
                    editableRoleOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={fieldLabelSx}>Status</Typography>
            <StatusSelect
              value={presenceValue}
              onChange={set("presence")}
              options={presenceOptions}
              disabled={isStatusLocked}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            width: "100%",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={fieldLabelSx}>Chat Limit</Typography>
            <FormControl fullWidth size="small" sx={columnSelectSx}>
              <Select
                value={chatLimitValue}
                onChange={set("chatLimit")}
                IconComponent={Dropdown}
                MenuProps={selectMenuProps}
                sx={peopleSelectSx}
              >
                {AGENT_CHAT_LIMIT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={fieldLabelSx}>Timezone</Typography>
            <FormControl fullWidth size="small" sx={columnSelectSx}>
              <Select
                value={timezoneValue}
                onChange={set("timezone")}
                IconComponent={Dropdown}
                MenuProps={selectMenuProps}
                sx={peopleSelectSx}
              >
                {timezoneOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>Groups</Typography>
          {optionsLoading ? (
            <AgentFormFieldSkeleton />
          ) : (
            <>
              <FormControl fullWidth size="small" sx={columnSelectSx}>
                <Select
                  displayEmpty
                  value={form.groupSelect || ""}
                  onChange={handleGroupSelect}
                  IconComponent={Dropdown}
                  MenuProps={selectMenuProps}
                  sx={peopleSelectSx}
                  renderValue={(v) => (
                    <Typography
                      component="span"
                      sx={{ color: v ? "#061522" : "#C5CED6", fontSize: 14 }}
                    >
                      {v ? groupLabel(v) : "Select Group"}
                    </Typography>
                  )}
                >
                  {groupOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {(form.groupIds || []).length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {(form.groupIds || []).map((id) => (
                    <SelectionChip
                      key={id}
                      label={groupLabel(id)}
                      // Super Admin (admin_with_billing) cannot be removed from any group —
                      // including when editing themselves.
                      onRemove={
                        isSuperAdmin
                          ? undefined
                          : () =>
                              onChange({
                                ...form,
                                groupIds: form.groupIds.filter((gid) => gid !== id),
                              })
                      }
                    />
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>Teams</Typography>
          {optionsLoading ? (
            <AgentFormFieldSkeleton />
          ) : (
            <>
              <FormControl fullWidth size="small" sx={columnSelectSx}>
                <Select
                  displayEmpty
                  value={form.teamSelect || ""}
                  onChange={handleTeamSelect}
                  IconComponent={Dropdown}
                  MenuProps={selectMenuProps}
                  sx={peopleSelectSx}
                  renderValue={(v) => (
                    <Typography
                      component="span"
                      sx={{ color: v ? "#061522" : "#C5CED6", fontSize: 14 }}
                    >
                      {v ? teamLabel(v) : "Select Team"}
                    </Typography>
                  )}
                >
                  {teamOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {(form.teamIds || []).length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {(form.teamIds || []).map((id) => (
                    <SelectionChip
                      key={id}
                      label={teamLabel(id)}
                      onRemove={() =>
                        onChange({
                          ...form,
                          teamIds: form.teamIds.filter((tid) => tid !== id),
                        })
                      }
                    />
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>Knowledge Base Role</Typography>
          <FormControl fullWidth size="small" sx={columnSelectSx}>
            <Select
              displayEmpty
              value={form.kbRole || ""}
              onChange={set("kbRole")}
              IconComponent={Dropdown}
              MenuProps={selectMenuProps}
              sx={peopleSelectSx}
              renderValue={(v) => (
                <Typography
                  component="span"
                  sx={{ color: v ? "#061522" : "#C5CED6", fontSize: 14 }}
                >
                  {v
                    ? AGENT_KB_ROLE_OPTIONS.find((o) => o.value === v)?.label || v
                    : "Select KB role"}
                </Typography>
              )}
            >
              {AGENT_KB_ROLE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
}
